import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/realtime-js';

export type ChangelogEntry = {
  id: number;
  op: string;
  ts: string;
  authUid: string;
  authEmail: string | null;
  tableName: string;
  recordId: string;
  entityName: string | null;
  entityType: string;
  record: Record<string, unknown> | null;
  oldRecord: Record<string, unknown> | null;
};

type RawRow = {
  id: number;
  op: string;
  ts: string;
  auth_uid: string;
  table_name: string;
  record_id: string;
  entity_name: string | null;
  entity_type: string;
  record: Record<string, unknown> | null;
  old_record: Record<string, unknown> | null;
};

const PAGE_SIZE = 50;

const LINK_TABLE_NAMES: Record<string, { from: string; to: string; fromKey: string; toKey: string }> = {
  link_persona_situation: { from: 'persona', to: 'situation', fromKey: 'persona_id', toKey: 'situation_id' },
  link_persona_situation_pain: { from: 'persona-situation', to: 'pain', fromKey: 'persona_situation_id', toKey: 'pain_id' },
  link_solution_pain: { from: 'solution', to: 'pain', fromKey: 'solution_id', toKey: 'pain_id' },
  link_solution_journey: { from: 'solution', to: 'journey', fromKey: 'solution_id', toKey: 'journey_id' },
};

export function isLinkTable(tableName: string): boolean {
  return tableName in LINK_TABLE_NAMES;
}

export function getLinkTableInfo(tableName: string): { from: string; to: string; fromKey: string; toKey: string } | null {
  return LINK_TABLE_NAMES[tableName] ?? null;
}

export type NameMap = Record<string, string>;

export function buildEntityNameMap(entries: ChangelogEntry[]): NameMap {
  const map: NameMap = {};
  for (const e of entries) {
    if (!isLinkTable(e.tableName) && e.entityName) {
      // e.g. "persona:2" → "Product owner"
      map[`${e.entityType}:${e.recordId}`] = e.entityName;
    }
  }
  return map;
}

export function formatLinkOp(
  entry: ChangelogEntry,
  nameMap?: NameMap
): { text: string; title: string } {
  const link = LINK_TABLE_NAMES[entry.tableName];
  if (!link) return { text: '', title: '' };
  const data = entry.record ?? entry.oldRecord ?? {};
  const verb = entry.op === 'INSERT' ? 'Linked' : 'Unlinked';
  const fromId = data[link.fromKey] ?? '?';
  const toId = data[link.toKey] ?? '?';
  const fromName = nameMap?.[`${link.from}:${fromId}`];
  const toName = nameMap?.[`${link.to}:${toId}`];
  const text = `${verb} ${link.from} #${fromId} → ${link.to} #${toId}`;
  const titleParts: string[] = [];
  if (fromName) titleParts.push(`${link.from}: ${fromName}`);
  if (toName) titleParts.push(`${link.to}: ${toName}`);
  const title = titleParts.length > 0 ? titleParts.join(' → ') : text;
  return { text, title };
}

export function computeDiff(
  record: Record<string, unknown> | null,
  oldRecord: Record<string, unknown> | null
): Array<{ field: string; from: unknown; to: unknown }> {
  if (!record || !oldRecord) return [];
  const diffs: Array<{ field: string; from: unknown; to: unknown }> = [];
  const skipFields = new Set([
    'search_vector', 'organisation_id', 'created_by',
  ]);
  for (const key of Object.keys(record)) {
    if (skipFields.has(key)) continue;
    if (key.endsWith('_id')) continue;
    const newVal = record[key];
    const oldVal = oldRecord[key];
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      diffs.push({ field: key, from: oldVal, to: newVal });
    }
  }
  return diffs;
}

export function useProjectChangelog(projectId: number | null) {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);

  const resolveEmails = useCallback(async (uids: string[]): Promise<Record<string, string>> => {
    if (uids.length === 0) return {};
    const { data } = await supabase.rpc('get_user_emails', { p_uids: uids });
    if (!data) return {};
    const map: Record<string, string> = {};
    for (const row of data as Array<{ uid: string; email: string }>) {
      map[row.uid] = row.email;
    }
    return map;
  }, []);

  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    if (projectId == null) return;
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('project_changelog', {
        p_project_id: projectId,
        p_limit: PAGE_SIZE,
        p_offset: offset,
      });
      if (rpcError) throw new Error(rpcError.message);
      const rows = (data ?? []) as RawRow[];

      const uids = [...new Set(rows.map((r) => r.auth_uid).filter(Boolean))];
      const emailMap = await resolveEmails(uids);

      const mapped: ChangelogEntry[] = rows.map((r) => ({
        id: r.id,
        op: r.op,
        ts: r.ts,
        authUid: r.auth_uid,
        authEmail: r.auth_uid ? (emailMap[r.auth_uid] ?? r.auth_uid) : null,
        tableName: r.table_name,
        recordId: r.record_id,
        entityName: r.entity_name,
        entityType: r.entity_type,
        record: r.record,
        oldRecord: r.old_record,
      }));

      setEntries((prev) => (append ? [...prev, ...mapped] : mapped));
      setHasMore(rows.length === PAGE_SIZE);
      offsetRef.current = offset + rows.length;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [projectId, resolveEmails]);

  useEffect(() => {
    if (projectId == null) {
      setEntries([]);
      setHasMore(true);
      offsetRef.current = 0;
      return;
    }
    offsetRef.current = 0;
    fetchPage(0, false);
  }, [projectId, fetchPage]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchPage(offsetRef.current, true);
    }
  }, [isLoading, hasMore, fetchPage]);

  const refresh = useCallback(() => {
    offsetRef.current = 0;
    fetchPage(0, false);
  }, [fetchPage]);

  // Realtime: subscribe to audit_log INSERTs and auto-refresh
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedRefresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      refreshRef.current();
    }, 500);
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  // Refetch on tab focus
  useEffect(() => {
    if (projectId == null) return;
    function onVisible() {
      if (document.visibilityState === 'visible') refreshRef.current();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [projectId]);

  // Realtime channel with auto-reconnect
  useEffect(() => {
    if (projectId == null) return;

    let removed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    const MAX_RECONNECTS = 5;

    function subscribe() {
      const channel = supabase
        .channel(`audit-log-${projectId}-${Date.now()}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'audit_log' },
          () => { debouncedRefresh(); }
        )
        .subscribe((status) => {
          if (removed) return;
          if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
            attempts = 0;
            return;
          }
          if (
            status === REALTIME_SUBSCRIBE_STATES.CLOSED ||
            status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR ||
            status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT
          ) {
            supabase.removeChannel(channel);
            attempts++;
            if (attempts > MAX_RECONNECTS) return;
            const delay = Math.min(3000 * 2 ** (attempts - 1), 30000);
            reconnectTimer = setTimeout(() => {
              if (!removed) subscribe();
            }, delay);
          }
        });

      return channel;
    }

    const channel = subscribe();

    return () => {
      removed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      supabase.removeChannel(channel);
    };
  }, [projectId, debouncedRefresh]);

  return { entries, isLoading, error, hasMore, loadMore, refresh };
}
